import { useState } from 'react';
import { motion } from 'motion/react';

import type { PostDraft } from '@smartapply/shared';

import { buildCarouselFromDraft } from '../../lib/carousel-builder.js';

interface DraftCardProps {
  draft: PostDraft;
  index: number;
  selected?: boolean;
  onSelect?: () => void;
  /** Quando o draft já foi marcado como publicado, ISO timestamp da última publicação. */
  publishedAt?: string | null;
}

const HOOK_LABELS: Record<PostDraft['hookType'], { label: string; color: string; bg: string }> = {
  curiosity_gap: {
    label: 'curiosidade',
    color: 'var(--accent-saffron)',
    bg: 'var(--accent-saffron-bg)',
  },
  contrarian: {
    label: 'contrarian',
    color: 'var(--accent-crimson)',
    bg: 'var(--accent-crimson-bg)',
  },
  data: {
    label: 'dado',
    color: 'var(--brand-ink)',
    bg: 'var(--bg-paper-recessed)',
  },
  narrative: {
    label: 'narrativa',
    color: 'var(--accent-forest)',
    bg: 'var(--accent-forest-bg)',
  },
  question: {
    label: 'pergunta',
    color: 'var(--ink-secondary)',
    bg: 'var(--bg-paper-recessed)',
  },
  build_in_public: {
    label: 'build em público',
    color: 'var(--accent-saffron)',
    bg: 'var(--accent-saffron-bg)',
  },
  feature_launch: {
    label: 'lançamento',
    color: 'var(--accent-saffron)',
    bg: 'var(--accent-saffron-bg)',
  },
};

/**
 * Constrói o prompt para o user colar em ChatGPT/Claude/Gemini e gerar a
 * imagem do post. Formato validado em uso real: legenda completa seguida
 * de uma frase curta de instrução. Modelos de imagem (DALL-E, Imagen,
 * Nano-banana) performam melhor quando a instrução é direta e deixam a
 * legenda guiar contexto, em vez de uma lista de diretrizes prescritivas
 * que tendem a competir com o conteúdo do post.
 */
/**
 * Constrói prompt pra IA (ChatGPT, Claude, Gemini) gerar UMA imagem única
 * de acompanhamento do post (não carrossel — pro carrossel real use o botão
 * "Gerar carrossel" que renderiza via HTML+PDF programático).
 *
 * Formato validado em uso real: legenda completa + 1 frase curta de instrução.
 * Modelos de imagem (DALL-E, Imagen, Nano-banana) performam melhor quando a
 * instrução é direta e deixam a legenda guiar contexto.
 */
function buildImagePrompt(draft: PostDraft): string {
  const hashtagLine =
    draft.hashtags.length > 0
      ? '\n\n' + draft.hashtags.map((h) => `#${h}`).join(' ')
      : '';

  const textBlock = draft.text + hashtagLine;

  return `${textBlock}

---

Essa é a legenda para um post no LinkedIn. Crie a imagem para o post (1 imagem única, formato 1:1 ou 4:5 vertical, estilo editorial minimalista — sem gradiente, sem emoji decorativo, sem avatar de pessoa fake).`;
}

export function DraftCard({ draft, index, selected, onSelect, publishedAt }: DraftCardProps) {
  const [textCopied, setTextCopied] = useState(false);
  const [imagePromptCopied, setImagePromptCopied] = useState(false);
  const [carouselLoading, setCarouselLoading] = useState(false);
  const [carouselError, setCarouselError] = useState<string | null>(null);
  const hook = HOOK_LABELS[draft.hookType];

  function handleGenerateCarousel() {
    setCarouselError(null);
    setCarouselLoading(true);
    try {
      // Usa o conteúdo COMPLEMENTAR do carrossel (gerado pelo LLM com ângulo
      // diferente da legenda). Fallback pra `text` em drafts antigos que não
      // têm carouselContent (gerados antes do prompt v5.2).
      const source = draft.carouselContent && draft.carouselContent.trim().length > 0
        ? draft.carouselContent
        : draft.text;
      // Geração 100% local: heurística determinística quebra o texto em slides.
      // Zero chamada de API, zero custo de token, instantâneo, sem rate limit.
      const result = buildCarouselFromDraft(source);

      // Codifica o JSON em base64 URL-safe e passa direto no hash da URL.
      // Vantagem vs localStorage: zero dependência de storage cross-tab,
      // sobrevive a F5, URL pode ser bookmarcada/compartilhada.
      const json = JSON.stringify(result);
      const bytes = new TextEncoder().encode(json);
      let binary = '';
      for (let i = 0; i < bytes.length; i += 1) {
        binary += String.fromCharCode(bytes[i] as number);
      }
      const encoded = btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

      const url = `${window.location.origin}/#/carousel?d=${encoded}`;
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) {
        setCarouselError(
          'O navegador bloqueou a nova aba. Permita popups pra este site e tente de novo.',
        );
      }
    } catch (e) {
      setCarouselError(
        e instanceof Error ? e.message : 'Falha ao gerar carrossel.',
      );
    } finally {
      setCarouselLoading(false);
    }
  }

  async function handleCopyText() {
    try {
      const fullText =
        draft.text +
        (draft.hashtags.length > 0
          ? '\n\n' + draft.hashtags.map((h) => `#${h}`).join(' ')
          : '');
      await navigator.clipboard.writeText(fullText);
      setTextCopied(true);
      setTimeout(() => setTextCopied(false), 1800);
    } catch {
      /* permissões negadas — fallback silencioso */
    }
  }

  async function handleCopyImagePrompt() {
    try {
      await navigator.clipboard.writeText(buildImagePrompt(draft));
      setImagePromptCopied(true);
      setTimeout(() => setImagePromptCopied(false), 1800);
    } catch {
      /* fallback silencioso */
    }
  }

  return (
    <motion.article
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: Math.min(index * 0.06, 0.4), ease: 'easeOut' }}
      className="card-paper relative overflow-hidden rounded-sm py-6 pl-7 pr-6"
      style={{
        borderLeft: `3px solid ${hook.color}`,
        outline: selected ? `2px solid ${hook.color}` : 'none',
        outlineOffset: '4px',
      }}
    >
      <header className="flex items-baseline justify-between gap-3">
        <div className="flex items-baseline gap-3">
          <span
            className="inline-flex items-center rounded-sm px-1.5 py-0.5 font-mono text-[11px] font-semibold uppercase tracking-wider tabular-nums"
            style={{
              color: hook.color,
              backgroundColor: hook.bg,
              border: `1px solid ${hook.color}33`,
            }}
          >
            D.{(index + 1).toString().padStart(2, '0')}
          </span>
          <span
            className="font-mono text-[10px] uppercase tracking-[0.18em]"
            style={{ color: hook.color }}
          >
            hook · {hook.label}
          </span>
          {publishedAt && (
            <span
              className="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em]"
              style={{
                color: 'var(--accent-forest)',
                backgroundColor: 'var(--accent-forest-bg)',
                border: '1px solid var(--accent-forest-border)',
              }}
              title={`Publicado em ${new Date(publishedAt).toLocaleString('pt-BR')}`}
            >
              ✓ publicado
            </span>
          )}
        </div>
        <span className="font-mono text-xs tabular-nums text-[var(--ink-tertiary)]">
          {draft.charCount} chars
        </span>
      </header>

      <pre className="mt-4 whitespace-pre-wrap break-words font-serif text-[15px] leading-[1.6] text-[var(--ink-primary)]">
        {draft.text}
      </pre>

      {draft.hashtags.length > 0 && (
        <div className="mt-4 flex items-baseline gap-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
            tags ({draft.hashtags.length})
          </span>
          <p className="font-mono text-xs leading-relaxed text-[var(--ink-secondary)]">
            {draft.hashtags.map((h) => `#${h}`).join(' ')}
          </p>
        </div>
      )}

      <p className="mt-5 border-t border-[var(--border-subtle)] pt-3 font-sans text-xs italic text-[var(--ink-tertiary)]">
        {draft.rationale}
      </p>

      <PrePublishChecklist />

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-4">
          <button
            type="button"
            onClick={() => void handleCopyText()}
            className="font-sans text-xs text-[var(--accent-saffron)] underline-offset-4 hover:underline"
            title="Copia o texto do post + hashtags pra colar no LinkedIn"
          >
            {textCopied ? '✓ texto copiado' : 'Copiar texto'}
          </button>
          <button
            type="button"
            onClick={() => void handleCopyImagePrompt()}
            className="font-sans text-xs text-[var(--accent-forest)] underline-offset-4 hover:underline"
            title="Copia o texto + instrução pra colar em ChatGPT/Claude/Gemini e gerar uma imagem única pro post"
          >
            {imagePromptCopied ? '✓ prompt copiado' : 'Copiar prompt p/ gerar imagem'}
          </button>
          <button
            type="button"
            onClick={handleGenerateCarousel}
            disabled={carouselLoading}
            className="font-sans text-xs text-[var(--accent-saffron)] underline-offset-4 hover:underline disabled:opacity-50"
            title="Quebra o post em 6-8 slides estruturados, renderiza visualmente no estilo do app, e gera PDF pronto pra subir como document post no LinkedIn. Geração 100% local, sem gastar tokens de IA."
          >
            {carouselLoading ? 'gerando carrossel...' : 'Gerar carrossel (PDF)'}
          </button>
        </div>
        {onSelect && (
          <button
            type="button"
            onClick={onSelect}
            className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)] hover:text-[var(--ink-primary)]"
          >
            {selected ? '✓ marcado pra publicar' : 'marcar pra publicar'}
          </button>
        )}
      </div>
      {carouselError && (
        <p
          role="alert"
          className="mt-3 font-sans text-xs text-[var(--accent-crimson)]"
        >
          {carouselError}
        </p>
      )}
    </motion.article>
  );
}

/**
 * Checklist expansível com 5 passos antes de publicar no LinkedIn. Reflete
 * o que move o algoritmo 360Brew em 2026: primeiros 60 min decidem alcance,
 * comentários valem 15x like, posts AI-detected são downranked. Sem
 * persistência — só lembrete visual.
 */
function PrePublishChecklist() {
  const [open, setOpen] = useState(false);
  return (
    <details
      open={open}
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
      className="mt-4 border-t border-[var(--border-subtle)] pt-3"
    >
      <summary
        className="cursor-pointer list-none font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)] hover:text-[var(--ink-primary)]"
        aria-label="Abrir checklist antes de publicar"
      >
        {open ? '▾' : '▸'} antes de publicar (5 passos pra escapar do downrank)
      </summary>
      <ol className="mt-3 space-y-2 font-sans text-xs leading-relaxed text-[var(--ink-secondary)]">
        <li className="flex gap-2">
          <span className="font-mono text-[var(--accent-saffron)]">01.</span>
          <span>
            <strong className="text-[var(--ink-primary)]">Edite até soar 100% seu tom.</strong>{' '}
            Troque 2-3 frases por como você falaria. O 360Brew detecta lexical pobre e penaliza
            posts que parecem "ChatGPT médio".
          </span>
        </li>
        <li className="flex gap-2">
          <span className="font-mono text-[var(--accent-saffron)]">02.</span>
          <span>
            <strong className="text-[var(--ink-primary)]">Publique entre 8-11h ou 17-19h (horário BR).</strong>{' '}
            Primeiros 60 min decidem trajetória do post; horário de pico tem mais gente online pra
            engajar nessa janela.
          </span>
        </li>
        <li className="flex gap-2">
          <span className="font-mono text-[var(--accent-saffron)]">03.</span>
          <span>
            <strong className="text-[var(--ink-primary)]">Avise 3-5 pessoas próximas</strong>{' '}
            pra comentar (não curtir) nos primeiros 30 min. Comentário vale 15x like; comentário com
            15+ palavras vale 2.5x mais.
          </span>
        </li>
        <li className="flex gap-2">
          <span className="font-mono text-[var(--accent-saffron)]">04.</span>
          <span>
            <strong className="text-[var(--ink-primary)]">Comente em 2-3 posts de outros perfis hoje</strong>{' '}
            antes de publicar o seu. Posts de quem comenta em conteúdo da rede recebem mais
            distribuição. Alavanca real de alcance.
          </span>
        </li>
        <li className="flex gap-2">
          <span className="font-mono text-[var(--accent-saffron)]">05.</span>
          <span>
            <strong className="text-[var(--ink-primary)]">Responda cada comentário em até 2h.</strong>{' '}
            Conversa real estende a vida do post no feed (long comment threads = mais reach).
            Comentário seu também conta como interação.
          </span>
        </li>
      </ol>
      <p className="mt-3 font-sans text-[11px] italic text-[var(--ink-tertiary)]">
        Sem isso, mesmo o melhor texto morre. O algoritmo 2026 prioriza dwell time, saves e
        conversa real — não curtidas.
      </p>
    </details>
  );
}
